// src/lib/guest-links-api.js — CHANTIER LIEN COURT, côté ADMIN (navigateur).
//
// Quand Dimitri envoie un lien À LA MAIN à un voyageur (copier-coller, dicté au
// téléphone), il veut un lien COURT et propre `sejour.fullbnbzen.com/l/<code>`
// plutôt qu'une URL longue à identifiant technique.
//
// La table `guest_links` est en RLS **service_role only** : impossible de créer
// l'alias depuis le navigateur. Ce module appelle donc l'action serveur ADMIN
// `guest_link_shorten` (lib/guest-links-handlers.js, requireAdmin au routeur).
//
// 🔴 REPLI OBLIGATOIRE — `guestLinkUrl()` ne renvoie JAMAIS de chaîne vide et
// ne lève JAMAIS : sur n'importe quel échec (pas de session, réseau coupé,
// 4xx/5xx, JSON illisible, base indisponible, gate d'émission OFF) elle renvoie
// le LIEN LONG construit localement. Un bouton « copier » ne doit jamais poser
// un presse-papier vide ni un lien cassé.
//
// 🔴 FENÊTRE D'ACTIVATION UTILISATEUR — LE PIÈGE DE CE MODULE.
// `navigator.clipboard.writeText` (et le repli `document.execCommand('copy')`)
// exigent d'être appelés PENDANT le geste utilisateur. Safari/WebKit et Firefox
// REJETTENT une écriture déclenchée après un `await` : un handler qui attend la
// session puis le réseau avant d'écrire pose un presse-papier VIDE. Le repli
// « lien long » protège la VALEUR, pas l'ÉCRITURE.
// ➜ D'où `copyGuestLinkNow()` : elle écrit le lien LONG (ou le lien court déjà
//   connu) SYNCHRONEMENT, dans le geste, PUIS tente en silence de réécrire le
//   lien court quand il arrive. L'UI n'annonce QUE ce qui est réellement dans
//   le presse-papier. `guestLinkUrl()` reste utilisable hors geste (affichage).
//
// Les anciens liens longs déjà envoyés continuent de fonctionner à vie : le
// lien court est un ALIAS supplémentaire, jamais un remplacement destructif.
//
// ⚠️ PAIEMENT VOLONTAIREMENT EXCLU (triple barrière serveur + CHECK SQL +
// routeur front) : `payment` n'est pas un kind aliasable. Ne jamais l'ajouter
// à `GUEST_LINK_ADMIN_KINDS`.

import { supabase } from "../supabase"
import { resolveAppBaseUrl } from "./app-base-url"

// Les 5 kinds acceptés par le hub `/l/<code>`. MIROIR de GUEST_LINK_KINDS
// (lib/guest-links.js) et de internalPathForKind (src/GuestLinkRedirectPage.jsx).
export const GUEST_LINK_ADMIN_KINDS = ["form", "journey", "journey_ota", "livret", "recu"]

// kind → chemin interne LONG (routes servies À VIE, backward-compat).
const LONG_PATH_BY_KIND = {
  form: (t) => `/bienvenue/${t}`,
  journey: (t) => `/sejour/${t}`,
  journey_ota: (t) => `/sejour-ota/${t}`,
  livret: (t) => `/livret/r/${t}`,
  recu: (t) => `/recu/${t}`,
}

// ── ÉTAT DE SESSION (module) ────────────────────────────────────────────────
// 1. `_shortUrlCache` : (kind, token) → lien court déjà émis. L'émission est
//    IDEMPOTENTE côté serveur (même couple ⇒ même code) et le lien est À VIE :
//    mémoriser est sans risque de péremption. Bénéfice décisif : à la 2e copie
//    du même lien, le lien COURT est écrit SYNCHRONEMENT dans le geste — donc
//    correctement, y compris sur Safari/Firefox — et sans aucun réseau.
// 2. `_emitDisabled` : le serveur a répondu `fallback_reason:'emission_desactivee'`
//    (gate `FBZ_GUEST_LINKS_EMIT_ENABLED` OFF). Tant qu'il est OFF, aucune copie
//    ne peut donner un lien court : on COURT-CIRCUITE l'aller-retour réseau pour
//    toutes les copies suivantes de la session (zéro bénéfice, zéro latence).
//    Auto-guérison : le drapeau est re-sondé au prochain chargement de l'app,
//    donc poser la variable en prod suffit — aucune seconde variable à gérer.
const _shortUrlCache = new Map()
let _emitDisabled = false

function _cacheKey(kind, token) {
  return `${kind}|${token}`
}

/** Remise à zéro de l'état de session — TESTS uniquement (piège resetMocks). */
export function _resetGuestLinkClientState() {
  _shortUrlCache.clear()
  _emitDisabled = false
}

/**
 * Lien LONG d'un (kind, token) — le filet, construit 100 % côté client.
 * Domaine canonique FIGÉ (resolveAppBaseUrl → sejour.fullbnbzen.com en prod).
 *
 * @param {string} kind — l'un de GUEST_LINK_ADMIN_KINDS.
 * @param {string} token — jeton cible (UUID).
 * @returns {string} URL longue, ou '' si kind/token inexploitables.
 */
export function longGuestLinkUrl(kind, token) {
  const builder = LONG_PATH_BY_KIND[kind]
  const tok = typeof token === "string" ? token.trim() : ""
  if (!builder || !tok) return ""
  return `${resolveAppBaseUrl()}${builder(tok)}`
}

/**
 * Lien à distribuer au voyageur : COURT si l'émission réussit, LONG sinon.
 *
 * Idempotent par construction : le serveur réutilise l'alias existant du couple
 * (kind, target_token) — recopier deux fois le même lien donne le même code.
 * Le lien émis est À VIE (aucune expiration).
 *
 * @param {string} kind
 * @param {string} token
 * @returns {Promise<{url:string, short:boolean}>} — `url` vide UNIQUEMENT si le
 *   couple (kind, token) est lui-même inexploitable (rien à copier).
 */
export async function guestLinkUrl(kind, token) {
  const fallback = longGuestLinkUrl(kind, token)
  if (!fallback) return { url: "", short: false }
  // Alias déjà connu pour ce couple → aucun réseau (émission idempotente).
  const cached = _shortUrlCache.get(_cacheKey(kind, token))
  if (cached) return { url: cached, short: true }
  // Gate d'émission OFF (constaté serveur) → aucun réseau : le serveur ne peut
  // que renvoyer ce lien long, autant l'éviter.
  if (_emitDisabled) return { url: fallback, short: false }
  try {
    const { data } = await supabase.auth.getSession()
    const accessToken = data && data.session ? data.session.access_token : null
    if (!accessToken) return { url: fallback, short: false }

    const res = await fetch("/api/beds24-mapping?action=guest_link_shorten", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ kind, target_token: token }),
    })
    let body = null
    try { body = await res.json() } catch (_) { /* réponse non-JSON */ }
    if (!res.ok || !body || body.ok !== true || typeof body.url !== "string" || !body.url) {
      // Refus de forme, erreur serveur, réponse illisible → LIEN LONG.
      return { url: fallback, short: false }
    }
    if (body.fallback_reason === "emission_desactivee") {
      // Gate d'émission OFF : on le mémorise pour ne plus jamais interroger le
      // serveur de la session (une erreur DB transitoire, elle, ne pose RIEN —
      // seul ce motif explicite désarme les appels suivants).
      _emitDisabled = true
      return { url: fallback, short: false }
    }
    const short = body.short === true
    if (short) _shortUrlCache.set(_cacheKey(kind, token), body.url)
    return { url: body.url, short }
  } catch (e) {
    // Réseau coupé / fetch indisponible → LIEN LONG (jamais d'exception vers l'UI).
    console.error("[guest-links-api] repli lien long:", e && e.message ? e.message : e)
    return { url: fallback, short: false }
  }
}

/**
 * Copie un texte dans le presse-papier.
 * `navigator.clipboard` (contexte sécurisé) puis repli `document.execCommand`
 * (http local, vieux navigateurs, iframe sans permission).
 *
 * @param {string} text
 * @returns {Promise<boolean>} true si la copie a abouti.
 */
export async function copyTextToClipboard(text) {
  const value = typeof text === "string" ? text : ""
  if (!value) return false
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch (_e) {
    /* contexte non sécurisé / permission refusée → repli execCommand */
  }
  try {
    if (typeof document === "undefined") return false
    const textarea = document.createElement("textarea")
    textarea.value = value
    textarea.style.position = "fixed"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(textarea)
    return ok !== false
  } catch (e) {
    console.error("[guest-links-api] copie presse-papier impossible:", e && e.message)
    return false
  }
}

/**
 * 🔴 LA fonction à appeler depuis un handler de CLIC. À appeler en PREMIER,
 * sans aucun `await` avant elle : l'écriture presse-papier doit partir dans la
 * fenêtre d'activation utilisateur (Safari/WebKit et Firefox rejettent sinon).
 *
 * Séquence :
 *   1. SYNCHRONE, dans le geste — écrit le lien court DÉJÀ CONNU pour ce couple
 *      si on l'a en cache, sinon le lien LONG (toujours constructible côté
 *      client). Le presse-papier contient donc un lien valide immédiatement.
 *   2. En arrière-plan — demande le lien court au serveur, puis tente une
 *      réécriture SILENCIEUSE du presse-papier. Si le navigateur refuse cette
 *      seconde écriture (hors geste), on garde le lien long : il est valide et
 *      servi à vie. Le couple est mis en cache → la copie SUIVANTE du même lien
 *      posera le lien COURT synchronement, sur tous les navigateurs.
 *
 * @param {string} kind
 * @param {string} token
 * @returns {{url:string, settled:Promise<{url:string, short:boolean, copied:boolean}>}}
 *   `url` = ce qui vient d'être écrit (affichage immédiat possible) ;
 *   `settled` ne rejette JAMAIS et résout avec ce qui est RÉELLEMENT dans le
 *   presse-papier — l'UI ne doit annoncer que ça.
 */
export function copyGuestLinkNow(kind, token) {
  const fallback = longGuestLinkUrl(kind, token)
  if (!fallback) {
    // Couple inexploitable : rien à copier, on ne touche pas au presse-papier.
    return { url: "", settled: Promise.resolve({ url: "", short: false, copied: false }) }
  }

  const cached = _shortUrlCache.get(_cacheKey(kind, token))
  const immediate = cached || fallback
  // ⬇⬇ L'ÉCRITURE PART ICI, avant tout await : on est encore dans le geste. ⬇⬇
  const firstCopy = copyTextToClipboard(immediate)

  if (cached) {
    // Déjà court : rien à améliorer, aucun réseau.
    return {
      url: cached,
      settled: firstCopy.then((copied) => ({ url: cached, short: true, copied })),
    }
  }

  const settled = (async () => {
    const copiedLong = await firstCopy
    const { url, short } = await guestLinkUrl(kind, token)
    if (!short || url === fallback) {
      return { url: fallback, short: false, copied: copiedLong }
    }
    // Réécriture silencieuse — hors geste, donc BEST-EFFORT assumé.
    const upgraded = await copyTextToClipboard(url)
    return upgraded
      ? { url, short: true, copied: true }
      : { url: fallback, short: false, copied: copiedLong }
  })()

  return { url: fallback, settled }
}
